// Lazy-chunk load recovery (P0: stale-deploy race).
//
// index.html is served `no-store`, so a browser can hold a freshly-fetched
// index that references NEW content-hashed chunks (App-<hash>.js,
// AuthApp-<hash>.js) a moment before the CDN edge has them. The dynamic
// `import()` then 404s and rejects. Without a catch that rejection is swallowed
// and `root.render` never runs — the tab is left on a permanent silent blank.
//
// This module makes that self-heal: on a chunk-load rejection we hard-reload
// ONCE (guarded by a sessionStorage flag so it can never loop) to pull the
// current index + chunks; if it already reloaded and still fails, we render a
// visible, branded error instead of reloading again. The flag is cleared on a
// successful mount so a later, unrelated deploy race can recover again.

/** sessionStorage key guarding the single auto-reload. */
export const CHUNK_RELOAD_FLAG = 'orovia:chunk-reload'

// Cross-browser signatures for "a lazy chunk failed to load". Chrome/Vite emit
// "Failed to fetch dynamically imported module"; Firefox "error loading
// dynamically imported module"; Safari "Importing a module script failed";
// webpack-style bundlers "Loading chunk N failed".
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk|error loading dynamically imported module|importing a module script failed/i

/** True when `error` looks like a lazy chunk that 404'd / failed to load. */
export function isChunkLoadError(error: unknown): boolean {
  if (error == null) return false
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : String(error)
  return CHUNK_ERROR_PATTERN.test(message)
}

export interface ChunkRecoveryDeps {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  reload: () => void
  /** Renders the visible fallback UI into #root when reloading won't help. */
  renderError: () => void
}

/**
 * Recover from a lazy-chunk load failure. Reloads ONCE — guarded by the
 * sessionStorage flag — to fetch the current index + hashed chunks after a
 * deploy race. If the flag is already set (we reloaded and it still failed),
 * renders a visible error instead of reloading again, so it can never loop.
 */
export function recoverFromChunkError(deps: ChunkRecoveryDeps): void {
  const alreadyReloaded = deps.storage.getItem(CHUNK_RELOAD_FLAG) === '1'
  if (alreadyReloaded) {
    deps.renderError()
    return
  }
  deps.storage.setItem(CHUNK_RELOAD_FLAG, '1')
  deps.reload()
}

/** Clear the reload guard after a successful mount. */
export function clearChunkReloadFlag(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(CHUNK_RELOAD_FLAG)
}

/**
 * Wrap a dynamic-import render chain. On a chunk-load rejection, trigger the
 * single-reload recovery; anything that is NOT a chunk error is re-thrown so a
 * genuine runtime bug still surfaces (as it did before, unswallowed).
 */
export function guardChunkImport(
  promise: Promise<void>,
  deps: ChunkRecoveryDeps,
): Promise<void> {
  return promise.catch((error: unknown) => {
    if (isChunkLoadError(error)) {
      recoverFromChunkError(deps)
      return
    }
    throw error
  })
}
